import { Component, EventEmitter, Inject, Input, Output, LOCALE_ID } from '@angular/core';
import { formatDate } from '@angular/common';

import { Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { CommandRelay } from '../../../shared/commands/command-relay.class';

import { MatDialog } from '@angular/material/dialog';

import { JSR310ConversionService }  from '../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { MessageDialogComponent } from '../../../shared/dialogs/components/message-dialog.component';

import { UserTLR } from '../../humain/user/entities/user-tlr.entity';
import { UserService } from '../../humain/user/services/user.service';

import { MessageCollationneService } from '../services/message-collationne.service';
import { MessageCollationneTLR } from '../entities/message-collationne-tlr.entity';
import { MessageCollationneDTO } from '../entities/message-collationne-dto.entity';
import { MessageCollationneCreate } from '../entities/message-collationne-create.entity';

@Component({
	selector: 'create-message-collationne-dialog',
	templateUrl: 'create-message-collationne-dialog.html',
})
export class CreateMessageCollationneDialogComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	reinitEntitySubject = new Subject<void>();
	emetteurOptions = [
		{
			value: true,
			label: "Emetteur"
		},
		{
			value: false,
			label: "Récepteur"
		}
	];
	parcOptions = [
		{
			value: true,
			label: "En exploitation"
		},
		{
			value: false,
			label: "En construction"
		}
	];
	clearParcsCommands = new Subject<void>();
	getParcsEnConstructionSuggestionObservable = (criteria: string) => this.messageCollationneService.getSuggestionsParcsEnConstruction(criteria, 15);
	
	// Valeurs statiques chargees depuis le serveur
	user!: UserTLR;
	
	// Listes de valeurs poussees depuis le contexte du parent
	availableMessages!: MessageCollationneDTO[];
	
	// Saisie de l'utilisateur
	selectedOldMessage: MessageCollationneDTO | null = null;
	message!: MessageCollationneCreate;
	numMessage!: string;
	time!: Date;
	parcEnExploitation: boolean = true;
	
	// Fonctions d'aide au fonctionnement des composants
	getMessageLabel = (message: MessageCollationneDTO) => {
		let elements: string[] = [];
		let localDate = message.dateMessage;
		if (localDate) {
			let date = this.conversionService.fromMandatoryLocalDate(localDate);
			let dateStr = formatDate(date, "dd/MM/yyyy", this.locale);
			elements.push(dateStr);
		}
		
		if (message.correspondantNom) {
			elements.push(message.correspondantNom);
		}
		
		if (message.correspondantEntite) {
			elements.push(message.correspondantEntite);
		}
		
		if (message.correspondantNum) {
			elements.push(message.correspondantNum);
		}
		
		return elements.join(', ');
	};

	constructor( //
		private dialog: MatDialog,
		@Inject(LOCALE_ID) private locale: string,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
		@Inject(MessageCollationneService) private messageCollationneService: MessageCollationneService,
		@Inject(UserService) userService: UserService,
	) {
		let user$ = userService.getCurrentUserTLR();
		user$.subscribe(
			(user) => this.user = user
		);
		
		// A chaque fois qu'une reinitialisation de l'entite a saisir est demandee,
		// demander un id pour ce message
		let idMessage$ = this.reinitEntitySubject.pipe(
			mergeMap(
				() => this.messageCollationneService.getNextId()
			)
		);
		
		// Quand on a obtenu l'id pour le nouveau message et que l'utilisateur est charge, initialiser proprement l'entite et les champs
		combineLatest([user$, idMessage$]).subscribe(
			(data) => {
				let user = data[0];
				let idMessage = data[1];

				// Recreate de l'entite vierge						
				this.message = new MessageCollationneCreate();
				
				// Assingation de l'id
				this.message.id = idMessage;
		
				// Initialisation de la date de saisie (non-modifiable par l'utilisateur 
				let now = new Date();
				this.message.dateSaisie = this.conversionService.toMandatoryLocalDateTime(now);
		
				// Initialisation de la date du message (arrondi au 5 minutes inferieures)
				let date = new Date();
				date.setSeconds(0);
				date.setMinutes(date.getMinutes() - (date.getMinutes() % 5));
				this.message.dateMessage = this.conversionService.toMandatoryLocalDateTime(date);
				
				// Reinitialisation du radio button pour l'etat du parc
				this.parcEnExploitation = true;
		
				// Transmission de la date du message au champ servant a gerer la saisie de l'heure
				this.time = date;
				
				// Transmission du numer de message a l'affichage
				this.numMessage = this.messageCollationneService.getNumero(this.message);
				
				// Reinitialisation des parcs selectionnes
				this.clearParcsCommands.next();
				
				// Inscription des donnees de l'utilisateur dans l'entite
				this.message.agentId = user.id;
				this.message.agentFonction = user.role;
				this.message.agentEntite = "ENGIE Green";
			}
		);
	}
	
	// Liaison de l'observable servant a commander l'affichage du composant
	private openRelay = new CommandRelay<MessageCollationneDTO[]>(data => this.open(data));
	@Input() set openSubject(obs: Observable<MessageCollationneDTO[]> | undefined) {
		this.openRelay.register(obs);
	}

	// Evenements de validation de l'operation en cours
	@Output() messageCreate = new EventEmitter<MessageCollationneTLR>();

	open(availableMessages: MessageCollationneDTO[]) {
		this.availableMessages = availableMessages;
		
		// Effacement de l'eventuelle ancienne saisie
		this.selectedOldMessage = null;

		// Envoi du signal de reinitialisation pour evaluation des elements qui dependent de l'utilisateur connecte		
		this.reinitEntitySubject.next();

		// Affichage de la pop-up
		this.displaySubject.next(true);
	}
	
	onSelectOldMessage(message: MessageCollationneDTO) {
		this.selectedOldMessage = message;
		
		this.message.agentEntite = message.agentEntite;
		this.message.correspondantEntite = message.correspondantEntite;
		this.message.agentFonction = message.agentFonction;
		this.message.correspondantFonction = message.correspondantFonction;
		this.message.message = message.message;
		this.message.correspondantNom = message.correspondantNom;
		this.message.correspondantNum = message.correspondantNum;
		this.message.agentId = message.agentId;
		this.message.antenneId = message.antenneId;
		this.message.emetteur = message.emetteur;
	}
	
	onEmetteurChange(emetteur: boolean) {
		// Appliquer la valeur a l'entite des fois que le binding ne l'ai pas deja fait
		this.message.emetteur = emetteur;
		
		// Recalculer le numero du message
		this.numMessage = this.messageCollationneService.getNumero(this.message);
	}
	
	onTimeChange(newTime: Date) {
		// Lors d'un changement de l'heure par l'utilisateur, modifier la date du message
		if (!this.message.dateMessage) {
			// Normalement impossible car la date du message est initialisee des le open
			throw new Error();
		}
		this.message.dateMessage[3] = newTime.getHours();
		this.message.dateMessage[4] = newTime.getMinutes();
	}
	
	onParcIdsChange(parcIds: number[]) {
		this.message.parcIds = parcIds;
	}

	onCreate() {
		// Validation de la saisie utilisateur
		if (!this.validateInputs()) {
			return;
		}

		// Envoi de la demande de sauvegarde de la nouvelle version de l'entite
		this.messageCollationneService.createMessage(this.message) //
			.pipe(
				catchError(
					(err: any, _caught: Observable<MessageCollationneTLR>) => {
						alert("Erreur lors de l'enregistrement du message (Erreur " + err.status + ")");
						return of(null);
					}
				)
			)
			.subscribe(
				(message: MessageCollationneTLR | null) => {
					if (message) {
						alert("Votre message a été enregistré sous le numéro " + this.messageCollationneService.getNumero(message));
						this.messageCreate.emit(message);
						this.displaySubject.next(false);
					}
				}
			);
	}

	private validateInputs(): boolean {
		let erreurs: string[] = [];

		// Verifier qu'il y a un moins un parc selectionne
		if (this.parcEnExploitation) {
			if (!this.message.parcIds || this.message.parcIds.length === 0) {
				erreurs.push("Vous devez sélectionner au moins un parc pour ce message.");					
			}
		} else {
			if (!this.message.parcEnConstruction || this.message.parcEnConstruction.length === 0) {
				erreurs.push("Vous devez saisir le nom du parc en construction pour ce message.");					
			}
		}

		if (erreurs.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Création impossible',
					messages: erreurs
				}
			});

			return false;
		}

		return true;
	}

	onCancelRequested() {
		this.displaySubject.next(false);
	}
}